import React, { useState, useEffect, useRef } from 'react';
import { FaPlus, FaEdit, FaTrash, FaSearch, FaImage, FaBox, FaShoppingCart, FaTruck, FaEye, FaEyeSlash, FaStar, FaTimes, FaSave, FaUpload } from 'react-icons/fa';
import { partsStoreService } from '../../services/partsStoreService';

const CATEGORIES = [
  'OBD Scanners & Diagnostics',
  'Engine Parts',
  'Brake Parts',
  'Suspension & Steering',
  'Electrical & Lighting',
  'Fluids & Chemicals',
  'Filters & Maintenance',
  'Interior Accessories',
  'Exterior Accessories',
  'Performance Parts',
  'Tools & Equipment',
  'Towing & Cargo',
  'Wheels & Tires',
  'Other'
];

const PartsStoreManager = () => {
  const [activeView, setActiveView] = useState('products');
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const emptyProduct = {
    name: '',
    description: '',
    price: '',
    sale_price: '',
    category: 'Other',
    subcategory: '',
    sku: '',
    stock_quantity: 0,
    images: [],
    specifications: {},
    weight_lbs: '',
    compatible_makes: [],
    condition: 'new',
    featured: false,
    status: 'draft'
  };

  const [formData, setFormData] = useState(emptyProduct);
  const [specKey, setSpecKey] = useState('');
  const [specValue, setSpecValue] = useState('');

  useEffect(() => {
    loadData();
  }, [activeView]);

  const loadData = async () => {
    setLoading(true);
    if (activeView === 'products') {
      const { data } = await partsStoreService.getAllProducts();
      setProducts(data);
    } else {
      const { data } = await partsStoreService.getOrders();
      setOrders(data);
    }
    setLoading(false);
  };

  const openCreateModal = () => {
    setEditingProduct(null);
    setFormData(emptyProduct);
    setShowModal(true);
  };

  const openEditModal = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name || '',
      description: product.description || '',
      price: product.price || '',
      sale_price: product.sale_price || '',
      category: product.category || 'Other',
      subcategory: product.subcategory || '',
      sku: product.sku || '',
      stock_quantity: product.stock_quantity || 0,
      images: product.images || [],
      specifications: product.specifications || {},
      weight_lbs: product.weight_lbs || '',
      compatible_makes: product.compatible_makes || [],
      condition: product.condition || 'new',
      featured: product.featured || false,
      status: product.status || 'draft'
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.price) {
      alert('Name and price are required.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...formData,
        price: parseFloat(formData.price),
        sale_price: formData.sale_price ? parseFloat(formData.sale_price) : null,
        stock_quantity: parseInt(formData.stock_quantity) || 0,
        weight_lbs: formData.weight_lbs ? parseFloat(formData.weight_lbs) : null
      };

      if (editingProduct) {
        await partsStoreService.updateProduct(editingProduct.id, payload);
      } else {
        await partsStoreService.createProduct(payload);
      }
      setShowModal(false);
      loadData();
    } catch (err) {
      alert('Error saving product: ' + err.message);
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    await partsStoreService.deleteProduct(id);
    loadData();
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);

    const tempId = editingProduct?.id || 'new-' + Date.now();
    const newImages = [...formData.images];

    for (const file of files) {
      const { url, error } = await partsStoreService.uploadProductImage(file, tempId);
      if (url && !error) {
        newImages.push(url);
      }
    }

    setFormData(prev => ({ ...prev, images: newImages }));
    setUploading(false);
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const addSpec = () => {
    if (!specKey.trim()) return;
    setFormData(prev => ({
      ...prev,
      specifications: { ...prev.specifications, [specKey.trim()]: specValue.trim() }
    }));
    setSpecKey('');
    setSpecValue('');
  };

  const removeSpec = (key) => {
    setFormData(prev => {
      const specs = { ...prev.specifications };
      delete specs[key];
      return { ...prev, specifications: specs };
    });
  };

  const handleOrderUpdate = async (orderId, updates) => {
    await partsStoreService.updateOrder(orderId, updates);
    loadData();
    setSelectedOrder(null);
  };

  const filteredProducts = products.filter(p =>
    p.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.sku?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.category?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatPrice = (p) => p ? `$${parseFloat(p).toFixed(2)}` : '—';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-[22px] font-semibold tracking-[-0.01em] text-adm-text-hi">Parts Store</h1>
          <p className="text-gray-500 text-sm mt-1">Manage products and orders</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setActiveView('products')}
            className={`px-3 py-1.5 rounded-adm-ctl text-sm font-medium transition-colors ${activeView === 'products' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
          >
            <FaBox className="inline mr-2" />Products
          </button>
          <button
            onClick={() => setActiveView('orders')}
            className={`px-3 py-1.5 rounded-adm-ctl text-sm font-medium transition-colors ${activeView === 'orders' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
          >
            <FaShoppingCart className="inline mr-2" />Orders
          </button>
        </div>
      </div>

      {/* =================== PRODUCTS VIEW =================== */}
      {activeView === 'products' && (
        <>
          <div className="flex flex-col md:flex-row gap-3 items-center justify-between">
            <div className="relative flex-1 max-w-md">
              <FaSearch className="absolute left-3 top-3 text-gray-400" />
              <input
                type="text"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <button
              onClick={openCreateModal}
              className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2.5 rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              <FaPlus /> Add Product
            </button>
          </div>

          {loading ? (
            <div className="text-center py-20 text-gray-500">Loading products...</div>
          ) : filteredProducts.length === 0 ? (
            <div className="text-center py-20">
              <FaBox className="mx-auto text-5xl text-gray-300 mb-4" />
              <p className="text-gray-500 text-lg">No products yet</p>
              <button onClick={openCreateModal} className="mt-4 text-blue-600 hover:underline font-medium">Add your first product →</button>
            </div>
          ) : (
            <div className="bg-adm-bg-1 rounded-adm-card shadow-adm-card border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table>
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th>Product</th>
                      <th>Category</th>
                      <th>SKU</th>
                      <th className="adm-num">Price</th>
                      <th className="text-center">Stock</th>
                      <th className="text-center">Status</th>
                      <th className="adm-num">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredProducts.map((product) => (
                      <tr key={product.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                              {product.images?.[0] ? (
                                <img src={product.images[0]} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <FaImage className="text-gray-300" />
                                </div>
                              )}
                            </div>
                            <div>
                              <p className="font-medium text-gray-900 text-sm">{product.name}</p>
                              {product.featured && <span className="text-xs text-amber-600">★ Featured</span>}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">{product.category}</td>
                        <td className="px-4 py-3 text-sm text-gray-500 font-mono">{product.sku || '—'}</td>
                        <td className="px-4 py-3 text-right">
                          {product.sale_price ? (
                            <div>
                              <span className="text-gray-400 line-through text-xs">{formatPrice(product.price)}</span>
                              <span className="text-green-600 font-semibold text-sm ml-1">{formatPrice(product.sale_price)}</span>
                            </div>
                          ) : (
                            <span className="font-semibold text-sm">{formatPrice(product.price)}</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`text-sm font-medium ${product.stock_quantity <= 0 ? 'text-red-600' : product.stock_quantity <= 5 ? 'text-amber-600' : 'text-green-600'}`}>
                            {product.stock_quantity}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            product.status === 'active' ? 'bg-green-100 text-green-700' :
                            product.status === 'draft' ? 'bg-gray-100 text-gray-600' :
                            product.status === 'out_of_stock' ? 'bg-red-100 text-red-700' :
                            'bg-gray-100 text-gray-500'
                          }`}>
                            {product.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button onClick={() => openEditModal(product)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg" title="Edit">
                              <FaEdit />
                            </button>
                            <button onClick={() => handleDelete(product.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg" title="Delete">
                              <FaTrash />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* =================== ORDERS VIEW =================== */}
      {activeView === 'orders' && (
        <>
          {loading ? (
            <div className="text-center py-20 text-gray-500">Loading orders...</div>
          ) : orders.length === 0 ? (
            <div className="text-center py-20">
              <FaShoppingCart className="mx-auto text-5xl text-gray-300 mb-4" />
              <p className="text-gray-500 text-lg">No orders yet</p>
            </div>
          ) : (
            <div className="bg-adm-bg-1 rounded-adm-card shadow-adm-card border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table>
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th>Order</th>
                      <th>Customer</th>
                      <th>Items</th>
                      <th className="adm-num">Total</th>
                      <th className="text-center">Payment</th>
                      <th className="text-center">Fulfillment</th>
                      <th>Date</th>
                      <th className="adm-num">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {orders.map((order) => (
                      <tr key={order.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-mono font-medium text-blue-600">{order.order_number}</td>
                        <td className="px-4 py-3">
                          <p className="text-sm font-medium">{order.customer_name}</p>
                          <p className="text-xs text-gray-500">{order.customer_email}</p>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {order.items?.length || 0} item{(order.items?.length || 0) !== 1 ? 's' : ''}
                        </td>
                        <td className="px-4 py-3 text-right font-semibold text-sm">{formatPrice(order.total)}</td>
                        <td className="px-4 py-3 text-center">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            order.payment_status === 'paid' ? 'bg-green-100 text-green-700' :
                            order.payment_status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                            order.payment_status === 'failed' ? 'bg-red-100 text-red-700' :
                            'bg-gray-100 text-gray-600'
                          }`}>
                            {order.payment_status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            order.fulfillment_status === 'delivered' || order.fulfillment_status === 'picked_up' ? 'bg-green-100 text-green-700' :
                            order.fulfillment_status === 'shipped' ? 'bg-blue-100 text-blue-700' :
                            order.fulfillment_status === 'processing' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-gray-100 text-gray-600'
                          }`}>
                            {order.fulfillment_status?.replace('_', ' ')}
                          </span>
                        </td>
                        <td>{new Date(order.created_at).toLocaleDateString()}</td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => setSelectedOrder(order)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                          >
                            <FaEye />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* =================== PRODUCT MODAL =================== */}
      {showModal && (
        <div className="fixed inset-0 bg-[var(--adm-scrim)] backdrop-blur-[2px] z-[9999] flex items-start justify-center pt-10 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl mx-4 mb-10">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="text-lg font-bold">{editingProduct ? 'Edit Product' : 'Add Product'}</h2>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-lg"><FaTimes /></button>
            </div>

            <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
              {/* Name & SKU */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Product Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g. Foxwell NT301 OBD2 Scanner"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">SKU</label>
                  <input
                    type="text"
                    value={formData.sku}
                    onChange={(e) => setFormData(prev => ({ ...prev, sku: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                    placeholder="PP-SCAN-001"
                  />
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={4}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  placeholder="Product description..."
                />
              </div>

              {/* Price, Sale Price, Weight */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Price *</label>
                  <div className="relative">
                    <span className="absolute left-3 top-2 text-gray-400">$</span>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.price}
                      onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg pl-7 pr-3 py-2 focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sale Price</label>
                  <div className="relative">
                    <span className="absolute left-3 top-2 text-gray-400">$</span>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.sale_price}
                      onChange={(e) => setFormData(prev => ({ ...prev, sale_price: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg pl-7 pr-3 py-2 focus:ring-2 focus:ring-blue-500"
                      placeholder="Leave blank if no sale"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Weight (lbs)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={formData.weight_lbs}
                    onChange={(e) => setFormData(prev => ({ ...prev, weight_lbs: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Category, Condition, Stock */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  >
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Condition</label>
                  <select
                    value={formData.condition}
                    onChange={(e) => setFormData(prev => ({ ...prev, condition: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="new">New</option>
                    <option value="used">Used</option>
                    <option value="refurbished">Refurbished</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Stock Quantity</label>
                  <input
                    type="number"
                    value={formData.stock_quantity}
                    onChange={(e) => setFormData(prev => ({ ...prev, stock_quantity: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Status & Featured */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="draft">Draft</option>
                    <option value="active">Active (Visible)</option>
                    <option value="out_of_stock">Out of Stock</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.featured}
                      onChange={(e) => setFormData(prev => ({ ...prev, featured: e.target.checked }))}
                      className="w-5 h-5 text-blue-600 rounded"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      <FaStar className="inline text-amber-500 mr-1" />
                      Featured Product
                    </span>
                  </label>
                </div>
              </div>

              {/* Images */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Product Images</label>
                <div className="flex flex-wrap gap-3">
                  {formData.images.map((img, i) => (
                    <div key={i} className="relative w-24 h-24 rounded-lg overflow-hidden border border-gray-200 group">
                      <img src={img} alt="" className="w-full h-full object-cover" />
                      <button
                        onClick={() => removeImage(i)}
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="w-24 h-24 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center text-gray-400 hover:border-blue-400 hover:text-blue-500 transition-colors"
                  >
                    {uploading ? (
                      <span className="text-xs">Uploading...</span>
                    ) : (
                      <>
                        <FaUpload className="mb-1" />
                        <span className="text-xs">Upload</span>
                      </>
                    )}
                  </button>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageUpload}
                  className="hidden"
                />
              </div>

              {/* Specifications */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Specifications</label>
                {Object.entries(formData.specifications).length > 0 && (
                  <div className="mb-3 space-y-1">
                    {Object.entries(formData.specifications).map(([key, val]) => (
                      <div key={key} className="flex items-center justify-between bg-gray-50 px-3 py-1.5 rounded text-sm">
                        <span><strong>{key}:</strong> {val}</span>
                        <button onClick={() => removeSpec(key)} className="text-red-500 text-xs hover:underline">Remove</button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Spec name"
                    value={specKey}
                    onChange={(e) => setSpecKey(e.target.value)}
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                  <input
                    type="text"
                    placeholder="Value"
                    value={specValue}
                    onChange={(e) => setSpecValue(e.target.value)}
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                  <button onClick={addSpec} className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium">Add</button>
                </div>
              </div>

              {/* Compatible Makes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Compatible Makes</label>
                <input
                  type="text"
                  value={formData.compatible_makes?.join(', ') || ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    compatible_makes: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                  }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  placeholder="BMW, Audi, Volkswagen (comma-separated)"
                />
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t bg-gray-50 rounded-b-2xl flex justify-end gap-3">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 text-gray-600 hover:text-gray-800">Cancel</button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50"
              >
                <FaSave /> {saving ? 'Saving...' : 'Save Product'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* =================== ORDER DETAIL MODAL =================== */}
      {selectedOrder && (
        <div className="fixed inset-0 bg-[var(--adm-scrim)] backdrop-blur-[2px] z-[9999] flex items-start justify-center pt-10 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 mb-10">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="text-lg font-bold">Order {selectedOrder.order_number}</h2>
              <button onClick={() => setSelectedOrder(null)} className="p-2 hover:bg-gray-100 rounded-lg"><FaTimes /></button>
            </div>

            <div className="space-y-5">
              {/* Customer Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500 uppercase font-semibold mb-1">Customer</p>
                  <p className="font-medium">{selectedOrder.customer_name}</p>
                  <p className="text-sm text-gray-600">{selectedOrder.customer_email}</p>
                  {selectedOrder.customer_phone && <p className="text-sm text-gray-600">{selectedOrder.customer_phone}</p>}
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase font-semibold mb-1">Shipping Address</p>
                  {selectedOrder.shipping_address ? (
                    <div className="text-sm text-gray-600">
                      <p>{selectedOrder.shipping_address.address}</p>
                      <p>{selectedOrder.shipping_address.city}, {selectedOrder.shipping_address.state} {selectedOrder.shipping_address.zip}</p>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">Local Pickup</p>
                  )}
                </div>
              </div>

              {/* Items */}
              <div>
                <p className="text-xs text-gray-500 uppercase font-semibold mb-2">Items</p>
                <div className="space-y-2">
                  {selectedOrder.items?.map((item, i) => (
                    <div key={i} className="flex justify-between items-center bg-gray-50 px-3 py-2 rounded-lg text-sm">
                      <span>{item.name} × {item.quantity}</span>
                      <span className="font-medium">{formatPrice(item.price * item.quantity)}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-3 pt-3 border-t space-y-1 text-sm">
                  <div className="flex justify-between"><span className="text-gray-500">Subtotal</span><span>{formatPrice(selectedOrder.subtotal)}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Tax</span><span>{formatPrice(selectedOrder.tax_amount)}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Shipping</span><span>{formatPrice(selectedOrder.shipping_cost)}</span></div>
                  <div className="flex justify-between font-bold text-base pt-1 border-t"><span>Total</span><span>{formatPrice(selectedOrder.total)}</span></div>
                </div>
              </div>

              {/* Fulfillment Update */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Fulfillment Status</label>
                  <select
                    value={selectedOrder.fulfillment_status}
                    onChange={(e) => handleOrderUpdate(selectedOrder.id, { fulfillment_status: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  >
                    <option value="unfulfilled">Unfulfilled</option>
                    <option value="processing">Processing</option>
                    <option value="shipped">Shipped</option>
                    <option value="delivered">Delivered</option>
                    <option value="pickup_ready">Pickup Ready</option>
                    <option value="picked_up">Picked Up</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tracking Number</label>
                  <input
                    type="text"
                    defaultValue={selectedOrder.tracking_number || ''}
                    onBlur={(e) => {
                      if (e.target.value !== (selectedOrder.tracking_number || '')) {
                        handleOrderUpdate(selectedOrder.id, { tracking_number: e.target.value });
                      }
                    }}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    placeholder="Enter tracking #"
                  />
                </div>
              </div>

              {selectedOrder.transaction_id && (
                <div>
                  <p className="text-xs text-gray-500">Transaction ID: <span className="font-mono">{selectedOrder.transaction_id}</span></p>
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t bg-gray-50 rounded-b-2xl flex justify-end">
              <button onClick={() => setSelectedOrder(null)} className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg font-medium">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartsStoreManager;
